<template>
  <div class="flex-1 flex flex-col max-w-xl mx-auto w-full gap-3 pb-2 sm:pb-4">
    <!-- Top Step Actions (Skip Step) -->
    <div v-if="canSkip" class="flex justify-end">
      <v-btn text small :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg text-xs font-bold capitalize" @click="skipStep">
        Skip Step
        <v-icon right x-small>mdi-chevron-double-right</v-icon>
      </v-btn>
    </div>

    <!-- Task Customer Badge Overview -->
    <!-- <div v-if="taskData" class="bg-indigo-50/60 rounded-xl p-3 border border-indigo-100 flex items-center justify-between text-xs shrink-0">
      <div>
        <span class="text-gray-500 block">Customer / Account</span>
        <span class="font-bold text-indigo-950 text-sm">{{ customerName }}</span>
      </div>
      <span class="bg-white text-indigo-700 font-extrabold px-2.5 py-1 rounded-lg border border-indigo-200">
        {{ caseNumber }}
      </span>
    </div> -->

    <!-- Camera / Existing Photo Container -->
    <div v-if="existingSelfieUrl && !retakeMode"
      class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center space-y-4 flex-1 flex flex-col justify-between">
      <div
        class="relative rounded-xl overflow-hidden shadow-inner flex-1 bg-gray-100 flex items-center justify-center min-h-[300px]">
        <img :src="existingSelfieUrl" class="w-full h-full object-cover rounded-xl" alt="Verified Selfie" />
        <div
          class="absolute top-3 right-3 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-md">
          <v-icon x-small color="white">mdi-check-circle</v-icon>
          Selfie Uploaded
        </div>
      </div>

      <div class="flex items-center gap-3 pt-2 shrink-0">
        <v-btn outlined :color="$vuetify.theme.currentTheme.employeePrimary" class="flex-1 rounded-xl h-10 text-xs font-bold" @click="retakeMode = true">
          <v-icon left small>mdi-camera-retake</v-icon>
          Retake Selfie
        </v-btn>

        <v-btn depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="flex-1 white--text rounded-xl h-10 text-xs font-bold shadow-xs"
          :loading="navigating" @click="proceedToNext">
          Proceed
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Active Camera View (Full Flex Height) -->
    <div v-else class="bg-white rounded-2xl p-2 shadow-sm border border-gray-100 flex-1 flex flex-col">
      <AppSelfieCamera :uploading="uploadingPhoto" :taskData="taskData" watermarkText="VISIT SELFIE"
        @captured="handleCapturedPhoto" />
    </div>
  </div>
</template>

<script>
import AppSelfieCamera from '@/app/components/common/AppSelfieCamera.vue'
import {
  getTaskById,
  getCollectionCaseById,
  uploadTaskPhoto,
  getTaskSettings,
  getOrgConfig
} from '@/app/services/dashboard'
import {
  sendStepLocation,
  computeTaskSteps,
  getNextStepRoute
} from '@/app/utils/taskFlow'

export default {
  name: 'AppTaskSelfie',
  components: {
    AppSelfieCamera
  },
  data() {
    return {
      taskId: this.$route.params.taskId,
      isCollection: this.$route.query.isCollection === 'true' || this.$route.query.isCollection === true,
      taskData: null,
      taskSettings: null,
      orgConfig: null,
      loadingTask: false,
      uploadingPhoto: false,
      navigating: false,
      retakeMode: false,
      stepsList: []
    }
  },
  computed: {
    customerName() {
      return (
        this.taskData?.customer?.customerFullName ||
        this.taskData?.customer?.name ||
        this.taskData?.taskTitle ||
        'Customer'
      )
    },
    caseNumber() {
      return this.taskData?.caseNo ? `#${this.taskData.caseNo}` : 'Task'
    },
    existingSelfieUrl() {
      const path = this.taskData?.additionalInfo?.selfie?.doc_physical_path || this.taskData?.selfieUrl
      if (!path) return null
      if (path.startsWith('http')) return path
      const s3Base = process.env.VUE_APP_S3_URL || 'https://finfi-prod.s3.ap-south-1.amazonaws.com'
      return `${s3Base}/${path}`
    },
    currentStepIndex() {
      const idx = this.stepsList.findIndex((s) => s.routeName === 'AppTaskSelfie')
      return idx >= 0 ? idx : 1
    },
    totalStepsCount() {
      return this.stepsList.length || 3
    },
    canSkip() {
      const current = this.stepsList.find((s) => s.routeName === 'AppTaskSelfie')
      return !!current?.canSkip
    }
  },
  mounted() {
    this.initStep()
  },
  methods: {
    async initStep() {
      try {
        this.loadingTask = true
        const [taskRes, settingsRes, orgRes] = await Promise.allSettled([
          getTaskById({ taskId: this.taskId, isCollection: this.isCollection })
            .catch(() => (this.isCollection ? getCollectionCaseById({ caseId: this.taskId }) : null)),
          getTaskSettings(),
          getOrgConfig()
        ])

        if (taskRes.status === 'fulfilled' && taskRes.value) {
          const resData =
            taskRes.value.data?.case ||
            taskRes.value.data?.task ||
            taskRes.value.data?.data?.case ||
            taskRes.value.data?.data?.task ||
            taskRes.value.data?.data ||
            taskRes.value.data
          this.taskData = resData
        }

        if (settingsRes.status === 'fulfilled') {
          this.taskSettings = settingsRes.value.data?.data || settingsRes.value.data
        }

        if (orgRes.status === 'fulfilled') {
          this.orgConfig = orgRes.value.data?.data || orgRes.value.data
        }

        this.stepsList = computeTaskSteps(this.taskSettings, this.orgConfig)
      } catch (err) {
        console.error('Error initializing selfie step:', err)
      } finally {
        this.loadingTask = false
      }
    },
    async handleCapturedPhoto({ file }) {
      try {
        this.uploadingPhoto = true
        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.taskId

        const formData = new FormData()
        formData.append('attachments', file)
        formData.append('taskId', validTaskId)
        if (this.isCollection) {
          formData.append('caseType', 'collection')
        }

        const uploadRes = await uploadTaskPhoto(formData)
        const uploadedPath =
          uploadRes?.data?.data?.doc_physical_path ||
          uploadRes?.data?.doc_physical_path ||
          uploadRes?.data?.data?.path ||
          uploadRes?.data?.path
        if (uploadedPath) {
          localStorage.setItem(`selfiePath-${this.taskId}`, uploadedPath)
          localStorage.setItem(`selfiePath-${validTaskId}`, uploadedPath)
        }
        this.$toast?.success?.('Selfie uploaded successfully!')

        // Send GPS coordinate on photo upload
        await sendStepLocation('AppTaskSelfie', this.taskData)

        this.proceedToNext()
      } catch (err) {
        console.error('Error uploading selfie:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to upload photo')
      } finally {
        this.uploadingPhoto = false
      }
    },
    skipStep() {
      this.proceedToNext()
    },
    proceedToNext() {
      const nextRouteName = getNextStepRoute('AppTaskSelfie', this.stepsList, this.taskData)
      this.$router.push({
        name: nextRouteName,
        params: { taskId: this.taskId },
        query: { isCollection: this.isCollection }
      })
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>
